import React from 'react'
import { getWeek, format, sub, getISOWeek, getQuarter, parseISO } from 'date-fns'
import { UIDropdown } from './../../../../components/forms/form-fields'
import ProductionReportWidgetService from '../../../../shared/services/ProductionReportWidget.service'
import UserProfileService from '../../../../shared/services/UserProfile.service'
import { fetchReportData } from './functions'
import getQueryParam from '../../../../shared/utilities/getQueryParam.function'
import makeQueryParams from '../../../../shared/utilities/makeQueryParams.function'

import './ProductionWidget.scss'

function getYearInfo(date = new Date()) {
  const year = date.getFullYear(),
    startOfYear = new Date(year, 0, 0),
    diff = date.getTime() - startOfYear.getTime(),
    oneDay = 1000 * 60 * 60 * 24,
    dayOfYear = Math.floor(diff / oneDay)

  const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
  const totalDaysInYear = isLeapYear ? 366 : 365

  return {
    dayOfYear,
    totalDaysInYear,
    daysRemaining: totalDaysInYear - dayOfYear,
  }
}

function calculateDailyRate(value, daysElapsed) {
  return daysElapsed <= 0 ? 0 : value / daysElapsed
}

function getExportLabel(timeSpan, iso) {
  switch (timeSpan) {
    case 'wtd':
      return `Week (${iso.isoWeekNumber})`
    case 'mtd':
      return `Month (${iso.isoMonth})`
    case 'qtd':
      return `Quarter (${iso.quarter})`
    case 'ytd':
      return 'Year'
    default:
      return timeSpan.toUpperCase() // Fallback
  }
}

export default class ProductionWidget extends React.Component {
  _searchMethod = null
  _searchDownline = false
  _searchId = null
  _reportConfig = { metric: 'points' }
  _enableUrlSync = false

  _updateQueryString = () => {
    if (!this._enableUrlSync) return

    const params = {}
    params.metric = this.state.metric
    if (this.Calc) params.report_date = format(this.Calc.getCurrDate(), 'yyyy-MM-dd')
    if (this.Calc) params.prev_year = this.Calc.getPrevYear()
    if (this._searchId && !isNaN(this._searchId) && this._searchId > 0) params.user_id = this._searchId

    const queryStr = makeQueryParams(params)
    const newUrl =
      window.location.protocol +
      '//' +
      window.location.host +
      window.location.pathname +
      (queryStr ? '?' + queryStr : '')
    window.history.pushState({ path: newUrl }, '', newUrl)
  }

  _initFromQueryParams = () => {
    const VALID_METRICS = [
      'points',
      'av',
      'av_no_aca',
      'aca_lives',
      'group',
      'enrolled',
      'ma_lives',
      'life_av',
      'med_supps_av',
    ]

    const reportDateParam = getQueryParam('report_date')
    if (reportDateParam) {
      try {
        const parsed = parseISO(reportDateParam)
        if (!isNaN(parsed.getTime()) && parsed <= new Date()) this.Calc.setCurrReportDate(parsed)
      } catch (ex) {}
    }

    const metricParam = getQueryParam('metric')
    if (metricParam && VALID_METRICS.includes(metricParam)) {
      this.setState({ metric: metricParam })
      this._setMetric(metricParam)
    }

    const prevYearParam = getQueryParam('prev_year')
    if (prevYearParam && !isNaN(prevYearParam) && parseInt(prevYearParam) > 0)
      this.Calc.setPrevReportYear(parseInt(prevYearParam))

    const userIdParam = getQueryParam('user_id')
    if (userIdParam && !isNaN(userIdParam) && parseInt(userIdParam) > 0) this._searchId = parseInt(userIdParam)

    return { userId: this._searchId }
  }

  _formatMetric = (value, canFloat) => {
    if (value !== null && isNaN(value)) return value

    switch (this.state.metric) {
      case 'av':
      case 'av_no_aca':
      case 'life_av':
      case 'med_supps_av':
      case 'group':
        return this._convertToCurrency(value)
      case 'aca_lives':
      case 'ma_lives':
      case 'points':
      case 'enrolled':
        return canFloat ? parseFloat(value).toFixed(2) : parseInt(value)
      default:
        return value
    }
  }

  _formatExportMetric = (value, metric, canFloat) => {
    if (value !== null && isNaN(value)) return value
    switch (metric) {
      case 'av':
      case 'av_no_aca':
      case 'life_av':
      case 'med_supps_av':
      case 'group':
        return this._convertToCurrency(value)
      case 'aca_lives':
      case 'ma_lives':
      case 'points':
      case 'enrolled':
        return canFloat ? parseFloat(value).toFixed(2) : parseInt(value)
      default:
        return value
    }
  }

  _forecastMetric = (currentValue, date) => {
    if (date && typeof date === 'string') {
      switch (date) {
        case 'prev_year':
          date = new Date()
          date.setFullYear(date.getFullYear() - 1)
          break
      }
    }

    const yearInfo = getYearInfo(date),
      dailyRate = calculateDailyRate(currentValue, yearInfo.dayOfYear)

    const metric = this.state.metric || this._reportConfig?.metric || 'points'
    const prevFullYearValue = parseFloat(this.state.data?.previousFullYear?.ytd?.[metric] || 0)
    const ytdGrowthPercent = parseFloat(this.state.data?.growth?.ytd?.[metric] || 0)
    const forecastedValue = prevFullYearValue * (1 + ytdGrowthPercent)
    const projectedGrowth = forecastedValue - currentValue

    return {
      currentValue,
      forecastedValue,
      dailyRate,
      daysRemaining: yearInfo.daysRemaining,
      projectedGrowth,
      dayOfYear: yearInfo.dayOfYear,
      totalDaysInYear: yearInfo.totalDaysInYear,
    }
  }

  _convertToCurrency = value => {
    if (isNaN(value) || value === null || value === undefined) {
      return '$0.00' // Default fallback
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(Math.round(parseFloat(value) * 100) / 100)
  }

  _canAdvanceDate = () => {
    if (this.state.reportDate) {
      if (format(this.state.reportDate, 'yyyy') < format(new Date(), 'yyyy')) return true
      if (format(this.state.reportDate, 'yyyy') === format(new Date(), 'yyyy'))
        return getWeek(this.state.reportDate, { weekStartsOn: 1 }) < getWeek(new Date(), { weekStartsOn: 1 })
      return false
    }

    return true
  }

  _canAdvanceExportDate = () => {
    if (this.state.exportReportDate) {
      if (format(this.state.exportReportDate, 'yyyy') < format(new Date(), 'yyyy')) return true
      if (format(this.state.exportReportDate, 'yyyy') === format(new Date(), 'yyyy'))
        return getWeek(this.state.exportReportDate, { weekStartsOn: 1 }) < getWeek(new Date(), { weekStartsOn: 1 })
      return false
    }

    return true
  }

  testAnnualDates = (currYear, prevYear) => {
    // store current values.
    let cD = this.Calc.getCurrDate(),
      cY = this.Calc.getCurrYear(),
      currSearch,
      prevSearch

    // update values to end of year.
    this.Calc.setCurrReportDate(new Date(currYear, 11, 31))
    this.Calc.setPrevReportYear(prevYear)

    // iterate day-to-day
    let results = []
    while (`${this.Calc.getCurrYear()}` === `${currYear}`) {
      // print findings.
      currSearch = { search: this.Calc.getCurrSearch() }
      prevSearch = { search: this.Calc.getPrevSearch() }
      results.push([
        currSearch.search.report_date,
        prevSearch.search.report_date,
        prevSearch.search.week,
        prevSearch.search.month,
        prevSearch.search.quarter,
        prevSearch.search.year,
      ])
      this.Calc.setCurrReportDate(sub(this.Calc.getCurrDate(), { days: 1 }))
    }

    // revert values.
    this.Calc.setCurrReportDate(cD)
    this.Calc.setPrevReportYear(cY)
    return results
  }

  testDates = () => {
    this.Calc.setCurrReportDate(new Date(2024, 11, 31))

    this.testAnnualDates(2024, 2023)
      .concat(this.testAnnualDates(2024, 2022))
      .concat(this.testAnnualDates(2024, 2021))
      .concat(this.testAnnualDates(2024, 2020))
      .concat(this.testAnnualDates(2023, 2022))
      .concat(this.testAnnualDates(2023, 2021))
      .concat(this.testAnnualDates(2023, 2020))
      .concat(this.testAnnualDates(2022, 2021))
      .concat(this.testAnnualDates(2022, 2020))
      .concat(this.testAnnualDates(2021, 2020))
  }

  _fetch = () => {
    if (this._searchMethod) {
      if (!isNaN(this._searchId) && this._searchId > 0) return this._fetchByDownline()

      this.setState({ ...this.Calc.getStateObj(), fetching: true })
      fetchReportData(this.Calc, {
        search: async search => {
          return await ProductionReportWidgetService[this._searchMethod](search)
        },
      }).then(data => {
        this.setState({ data, fetching: false, ...this.Calc.getStateObj() })
      })

      // 1. Enable this for comparative date testing.
      // 2. 'Copy Object' by right-clicking the console.log statement.
      // 3. Format as csv & save it to automation.usabg.com storage/apps folder.
      // 4. Visit automation.usabg.com/tests/date-comparisons, when logged in.
      // this.testDates();
    }
  }

  _fetchExportRecords = async () => {
    let calcObj = this.Calc.getStateObj()
    this.setState({ ...this.Calc.getStateObj(), fetching: true })

    let reportDate = format(calcObj.exportReportDate, 'yyyy-MM-dd')
    let previousDate = calcObj.exportPreviousReportDate

    let [currentData, previousData] = await Promise.all([
      ProductionReportWidgetService.search({
        search: {
          report_date: reportDate,
          export: 1,
        },
      }),
      ProductionReportWidgetService.search({
        search: {
          report_date: previousDate.report_date,
          week: previousDate.week,
          month: previousDate.month,
          quarter: previousDate.quarter,
          year: previousDate.year,
          export: 1,
        },
      }),
    ])

    currentData = this._reduceExportData(
      currentData.filter(cD => `${cD.timespan_ind}`.toUpperCase() === this.state.exportTimespanInd)
    )

    previousData = previousData
      .filter(cD => `${cD.timespan_ind}`.toUpperCase() === this.state.exportTimespanInd)
      .reduce((acc, item) => {
        if (item.source_id) {
          if (!acc[item.source_id]) {
            acc[item.source_id] = { timespan_data: {} }
          }

          // Store data by timespan_ind inside timespan_data
          acc[item.source_id].timespan_data[item.timespan_ind] = item
        } else if (item.userId) {
          // If userId exists, reduce based on userId
          acc[item.userId] = item
        }
        return acc
      }, {})

    this.setState(
      {
        previousData: previousData,
        currentData: currentData,
        ...this.Calc.getStateObj(),
        fetching: false,
      },
      () => {
        this._buildTableRecords(this.state.exportmetric)
      }
    )
  }

  // Only invoked in: self._fetchExportRecords(), ExportGrowthReportModal
  _buildTableRecords = metric => {
    let previousData = this.state.previousData,
      currentData = this.state.currentData,
      avTableRecord = []

    for (let key in previousData) {
      let exportData = [],
        shouldPush = false,
        timespanLabel = ''
      const timeSpanArray = ['wtd', 'mtd', 'qtd', 'ytd']

      if (previousData[key].timespan_data) {
        // The we have non legacy data
        for (let timeSpan of timeSpanArray) {
          // Skip if required data doesn't exist
          if (!previousData[key]?.timespan_data?.[timeSpan]) continue

          // Dynamically set timespan label and details for Week, Month, Quarter, and Year
          timespanLabel = getExportLabel(timeSpan, this._getDateInfoISO(this.state.exportReportDate))

          const currentMetric = currentData[key]?.timespan_data?.[timeSpan]?.[metric] ?? 0,
            previousMetric = previousData[key]?.timespan_data?.[timeSpan]?.[metric] ?? 0,
            data = {
              current: this._formatExportMetric(currentMetric, metric),
              previous: this._formatExportMetric(previousMetric, metric),
              id:
                (previousData[key]?.timespan_data?.[timeSpan]?.u_fname ?? '') +
                ' ' +
                (previousData[key]?.timespan_data?.[timeSpan]?.u_lname ?? ''),
              usertype: previousData[key]?.timespan_data?.[timeSpan]?.source_type ?? '',
              timespan: timespanLabel,
              growth: this._growth(currentMetric, previousMetric),
            }

          exportData.push(data)
          shouldPush = true
        }
      } else {
        timeSpanArray.forEach(timeSpan => {
          // Dynamically set timespan label and details for Week, Month, Quarter, and Year
          timespanLabel = getExportLabel(timeSpan, this._getDateInfoISO(this.state.exportReportDate))

          const currentMetric = currentData[key]?.timespan_data[timeSpan]?.[metric] ?? 0,
            previousMetric = previousData[key]?.[timeSpan]?.[metric] ?? 0,
            data = {
              current: this._formatExportMetric(currentMetric, metric),
              previous: this._formatExportMetric(previousMetric, metric),
              id: previousData[key]?.[timeSpan]?.agentname ?? '',
              usertype: previousData[key]?.[timeSpan]?.userType ?? '',
              timespan: timespanLabel,
              growth: this._growth(currentMetric, previousMetric),
            }

          // Check if either current or previous data is non-zero before pushing to exportData
          if (data.current !== 0 || data.previous !== 0) shouldPush = true

          exportData.push(data)
        })
      }

      // Now, check if any of the entries should be pushed (if at least one is non-zero)
      if (shouldPush) avTableRecord.push(...exportData)
    }

    // previousData.map
    this.setState({
      exportData: {
        ...this.state.exportData,
        rows: avTableRecord,
      },
    })
  }

  _reduceExportData = inputData => {
    let groupedData = {}

    inputData.forEach(entry => {
      const sourceId = entry.source_id

      if (!groupedData[sourceId]) {
        groupedData[sourceId] = {
          source_type: entry.source_type,
          report_date: entry.report_date,
          u_fname: entry.u_fname,
          u_lname: entry.u_lname,
          u_upline_id: entry.u_upline_id,
          timespan_data: {},
        }
      }

      groupedData[sourceId].timespan_data[entry.timespan_ind] = {
        points: entry.points,
        enrolled: entry.enrolled,
        aca_lives: entry.aca_lives,
        ma_lives: entry.ma_lives,
        av: entry.av,
        life_av: entry.life_av,
        med_supps_av: entry.med_supps_av,
        group: entry.group,
      }
    })

    return groupedData
  }

  _growth = (current, previous) => {
    let p = 0,
      c = 0

    try {
      p = previous
      p = p !== null && !isNaN(p) ? parseFloat(p) : 0
      c = current
      c = c !== null && !isNaN(c) ? parseFloat(c) : 0
    } catch (ex) {}

    // Calculate growth
    const growthValue = (c === 0 && p === 0 ? 0 : p === 0 ? 1 : (c - p) / p) * 100

    // Round to two decimal places and return as a string with percentage sign
    return growthValue.toFixed(2) + '%'
  }

  _getDateInfoISO = date => {
    const parsedDate = new Date(date)
    if (isNaN(parsedDate)) {
      throw new Error('Invalid date input')
    }

    const isoWeekNumber = getISOWeek(parsedDate)
    const quarter = getQuarter(parsedDate)
    const isoMonth = parsedDate.getMonth() + 1

    return {
      isoWeekNumber,
      quarter,
      isoMonth,
    }
  }

  toggleExportGrowthReportModal = async () => {
    const { isModalOpen, isLoading } = this.state

    // Toggle modal state
    this.setState({ isModalOpen: !isModalOpen })

    // Fetch data only if the modal is being opened and not already loading
    if (!isLoading && !isModalOpen) {
      this.setState({ isLoading: true, fetching: true })
      await this._fetchExportRecords()
      this.setState({ isLoading: false, fetching: false })
    }
  }

  _fetchByDownline = () => {
    if (this._searchMethod) {
      this.setState({ ...this.Calc.getStateObj(), fetching: true })
      fetchReportData(this.Calc, {
        search: async search => {
          if (this._searchMethod === 'searchCarrier') search.search.carrier_id = this._searchId
          else search.search.user_id = this._searchId
          return await ProductionReportWidgetService[this._searchMethod](search)
        },
      }).then(data => {
        this.setState({ data, fetching: false, ...this.Calc.getStateObj() })
      })
    }
  }

  // Used by DownlineGrowthReport (as a child widget, controlled by parent)
  _setMetric = metric => {
    // this._setMetric('points')
    // this._setMetric('av')
    // this._setMetric('aca_lives')
    // this._setMetric('group')
    // this._setMetric('enrolled')
    // this._setMetric('ma_lives')
    // this._setMetric('ma_lives')
    // this._setMetric('med_supps_av')
    if (metric) this._reportConfig.metric = metric
  }

  _onDateChange = reportDate => {
    if (this.Calc.setCurrReportDate(reportDate)) {
      this._searchDownline ? this._fetchByDownline() : this._fetch()
      this._updateQueryString()
    }
  }

  _onExportDateChange = exportReportDate => {
    if (this.Calc.setExportCurrReportDate(exportReportDate)) {
      this._fetchExportRecords()
    }
  }

  _onExportTimespanChange = timespanInd => {
    timespanInd = `${timespanInd}`.toUpperCase() || 'WTD'
    timespanInd = ['WTD', 'MTD', 'QTD', 'YTD'].includes(timespanInd) ? timespanInd : 'WTD'
    this.setState({ exportTimespanInd: timespanInd }, () => {
      this._fetchExportRecords()
    })
  }

  _onPreviousYearChange = e => {
    if (this.Calc.setPrevReportYear(parseInt(e.target.value))) {
      this._searchDownline ? this._fetchByDownline() : this._fetch()
      this._updateQueryString()
    }
  }

  _onExportPreviousYearChange = e => {
    if (this.Calc.setPrevReportYear(parseInt(e.target.value), true)) {
      this._fetchExportRecords()
    }
  }

  _renderPrevYearDropDown = () => {
    let year = format(this.state.reportDate, 'yyyy') - 1
    const years = Array.from(new Array(5), (v, i) => {
      return {
        text: year - i,
        value: year - i,
        checked: year - i === parseInt(this.state.prevYear),
      }
    })
    return (
      <UIDropdown
        options={years}
        selected={parseInt(this.state.prevYear)}
        onChange={this._onPreviousYearChange}
        value={parseInt(this.state.prevYear)}
      />
    )
  }

  _renderExportPrevYearDropDown = () => {
    let year = format(this.state.exportReportDate, 'yyyy') - 1
    const years = Array.from(new Array(5), (v, i) => {
      return {
        text: year - i,
        value: year - i,
        checked: year - i === parseInt(this.state.exportPrevYear),
      }
    })
    return (
      <UIDropdown
        options={years}
        selected={parseInt(this.state.exportPrevYear)}
        onChange={this._onExportPreviousYearChange}
        value={parseInt(this.state.exportPrevYear)}
      />
    )
  }
}
